import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl, API_BASE } from "@/api/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PackageCheck, PenLine, QrCode } from "lucide-react";
import DeliveryReceiptDialog from "@/components/DeliveryReceiptDialog";

/** Deep-link handoff: /app/handoff/:id — mobile scans the parcel QR, arrives here, opens Sign & Deliver. */
export default function HandoffPage() {
  const { id } = useParams();
  const [parcel, setParcel] = useState(null);
  const [openDeliver, setOpenDeliver] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/parcels/${id}`).then(r => setParcel(r.data)).catch(() => toast.error("Parcel not found"));
  }, [id]);

  if (!parcel) return <div className="p-10 overline">Loading…</div>;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-lg mx-auto space-y-4">
        <div className="text-center">
          <QrCode size={36} className="mx-auto text-primary" />
          <div className="overline mt-2">Parcel handoff</div>
          <h1 className="text-3xl font-display mt-1">Ready for handover.</h1>
        </div>
        <div className="border border-border rounded-md bg-card p-5 space-y-3 card-elev">
          <div className="flex items-center justify-between">
            <div className="overline">Tracking</div>
            <div className="mono text-primary">{parcel.tracking_number}</div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Kv k="Courier" v={parcel.courier} />
            <Kv k="Recipient" v={parcel.recipient_name} />
            <Kv k="Dept" v={parcel.recipient_department} />
            <Kv k="Items" v={parcel.item_count || 1} />
            <Kv k="Weight" v={`${parcel.weight_kg} kg`} />
            <Kv k="Type" v={parcel.parcel_type} />
          </div>
          {parcel.photo_url && <img src={fileUrl(parcel.photo_url)} alt="parcel" className="max-h-40 border border-border rounded-md mx-auto" />}
          {parcel.status === "delivered" ? (
            <div className="border border-primary/30 bg-primary/5 rounded-md p-3 text-center">
              <PackageCheck size={24} className="text-primary mx-auto mb-2" />
              <div className="font-semibold">Already delivered</div>
              <div className="text-xs text-muted-foreground mt-1">Signed by {parcel.signed_by || "—"}</div>
            </div>
          ) : (
            <Button data-testid="handoff-sign" onClick={() => setOpenDeliver(true)}
              className="w-full rounded-md bg-primary text-primary-foreground shadow-brut-orange h-11">
              <PenLine size={14} className="mr-2" /> Sign & Handover
            </Button>
          )}
        </div>
      </div>

      <DeliveryReceiptDialog open={openDeliver} onOpenChange={setOpenDeliver}
        parcel={parcel} onDelivered={()=>{ setOpenDeliver(false); nav("/app/parcels"); }} />
    </div>
  );
}

const Kv = ({ k, v }) => (
  <div>
    <div className="overline">{k}</div>
    <div className="font-semibold">{v || "—"}</div>
  </div>
);
